import type { SwitchChangeEventHandler } from 'antd/lib/switch';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useFieldArray, useFieldFast } from '../../../../../common/Form/hooks';
import type { CPScheduleDateRange, SetCPScheduleFormValues } from '../types';
import { CpScheduleFormFields } from '../types';
import { getCPWindowDateFromPreviousDate, getDefaultRange } from '../utils/form';
import { validateScheduleForm } from '../validation/scheduleForm';

type UseScheduleRanges = {
  dateRanges: CPScheduleDateRange[];
  addRange: () => void;
  removeRange: (index: number) => void;
  canRemove: boolean;
  timezone: string;
  replaceRange: (index: number, range: CPScheduleDateRange) => void;
  isDynamicStartDateSwitchDisabled: boolean;
  isDynamicEndDateSwitchDisabled: boolean;
  isDynamicStartDateEnabled: boolean;
  isDynamicEndDateEnabled: boolean;
  handleChangeIsDynamicStartDate: SwitchChangeEventHandler;
  handleChangeIsDynamicEndDate: SwitchChangeEventHandler;
};

export const useScheduleRanges = (onRemove: () => void, isFormDisabled: boolean): UseScheduleRanges => {
  const [, { value: dateRanges }, { push, removeAt, replaceAt }] = useFieldArray<CPScheduleDateRange>(
    CpScheduleFormFields.Ranges
  );
  const [, { value: timezone }] = useFieldFast<string>(CpScheduleFormFields.Timezone);
  const { values, setErrors } = useFormikContext<SetCPScheduleFormValues>();
  const [, { value: isDynamicStartDateEnabled }, { setValue: setIsDynamicStartDateEnabled }] = useFieldFast<boolean>(
    CpScheduleFormFields.IsDynamicStartDateEnabled
  );
  const [, { value: isDynamicEndDateEnabled }, { setValue: setIsDynamicEndDateEnabled }] = useFieldFast<boolean>(
    CpScheduleFormFields.IsDynamicEndDateEnabled
  );

  const isDynamicStartDateSwitchDisabled =
    isFormDisabled || (isDynamicEndDateEnabled && !isDynamicStartDateEnabled && dateRanges.length === 1);
  const isDynamicEndDateSwitchDisabled =
    isFormDisabled || (!isDynamicEndDateEnabled && isDynamicStartDateEnabled && dateRanges.length === 1);

  const handleChangeIsDynamicStartDate: SwitchChangeEventHandler = (value) => {
    setIsDynamicStartDateEnabled(value);

    setErrors(validateScheduleForm({ ...values, isDynamicStartDateEnabled: value }));
  };
  const handleChangeIsDynamicEndDate: SwitchChangeEventHandler = (value) => {
    setIsDynamicEndDateEnabled(value);

    setErrors(validateScheduleForm({ ...values, isDynamicEndDateEnabled: value }));
  };

  const addRange = useCallback((): void => {
    const end = dateRanges[dateRanges.length - 1].end;

    push(
      getDefaultRange(
        uuidv4(),
        getCPWindowDateFromPreviousDate(end, 0, timezone),
        getCPWindowDateFromPreviousDate(end, 1, timezone)
      )
    );
  }, [dateRanges, push, timezone]);

  const removeRange = useCallback(
    (index: number): void => {
      onRemove();
      removeAt(index);

      if (index === 0 && dateRanges.length === 2 && isDynamicStartDateEnabled) {
        setIsDynamicStartDateEnabled(false);
      }

      if (index === dateRanges.length - 1 && isDynamicEndDateEnabled) {
        setIsDynamicEndDateEnabled(false);
      }
    },
    [
      onRemove,
      removeAt,
      dateRanges.length,
      isDynamicStartDateEnabled,
      isDynamicEndDateEnabled,
      setIsDynamicStartDateEnabled,
      setIsDynamicEndDateEnabled,
    ]
  );

  return {
    dateRanges,
    addRange,
    removeRange,
    canRemove: dateRanges.length > 1,
    timezone,
    replaceRange: replaceAt,
    isDynamicStartDateEnabled,
    isDynamicEndDateEnabled,
    isDynamicEndDateSwitchDisabled,
    isDynamicStartDateSwitchDisabled,
    handleChangeIsDynamicEndDate,
    handleChangeIsDynamicStartDate,
  };
};
